'use client';
import React, { useEffect } from 'react';
import { Box, Grid } from '@mui/material';

import {
  CategoryChip,
  ErrorState,
  SportsFuturesCard,
  SportsFuturesFeedSkeleton,
} from '@/components';
import { DEFAULT_FUTURES_PARAMS } from '@/services/api/';
import { useSportsFutureCategories, useSportsFutures } from '@/services/query';
import { capitalizeAllWords } from '@/utils';

interface SportsFuturesFeedProps {
  selectedFutureTagId: number | null;
  setSelectedFutureTagId: (tagId: number) => void;
  mainContentScrollRef: React.RefObject<HTMLDivElement | null>;
}

export const SportsFuturesFeed = ({
  selectedFutureTagId,
  setSelectedFutureTagId,
  mainContentScrollRef,
}: SportsFuturesFeedProps) => {
  const {
    data: sportsCategories,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError,
    refetch: refetchCategories,
  } = useSportsFutureCategories();

  const {
    data: futureEvents,
    isLoading: isEventsLoading,
    isError: isEventsError,
    refetch: refetchEvents,
  } = useSportsFutures({
    tag_id: selectedFutureTagId,
    ...DEFAULT_FUTURES_PARAMS,
  });

  useEffect(() => {
    if (!selectedFutureTagId && sportsCategories && sportsCategories.length > 0) {
      setSelectedFutureTagId(sportsCategories[0].tagId);
    }
  }, [selectedFutureTagId, sportsCategories, setSelectedFutureTagId]);

  const handleRetry = () => {
    refetchCategories();
    refetchEvents();
  };

  const renderCategoryChip = (categoryObj: { category: string; tagId: number }) => {
    const isActive = selectedFutureTagId === categoryObj.tagId;
    const handleChipClick = () => setSelectedFutureTagId(categoryObj.tagId);

    return (
      <CategoryChip
        key={categoryObj.tagId}
        label={capitalizeAllWords(categoryObj.category)}
        isActive={isActive}
        onClick={handleChipClick}
      />
    );
  };

  const renderedCategoryChips = sportsCategories?.map(renderCategoryChip);

  if (isCategoriesLoading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          height: 'calc(100vh - 200px)',
          p: 1,
        }}
      >
        <SportsFuturesFeedSkeleton />
      </Box>
    );
  }

  if (isCategoriesError || isEventsError) {
    return (
      <ErrorState
        title="Failed to load sports futures"
        message="We encountered an issue fetching the latest futures. Please try again."
        onRetry={handleRetry}
      />
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      {/* Horizontal Category Filters - Fixed at Top */}
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          overflowX: 'auto',
          pb: 2,
          pt: 1,
          px: 1,
          flexShrink: 0,
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}
      >
        {renderedCategoryChips}
      </Box>

      {/* Scrollable Feed Area */}
      <Box
        ref={mainContentScrollRef}
        sx={{
          overflowY: 'auto',
          overflowX: 'hidden',
          p: 1,
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'divider',
            borderRadius: '4px',
            '&:hover': { bgcolor: 'text.secondary' },
          },
        }}
      >
        <EventsGrid
          isLoading={isEventsLoading}
          events={futureEvents}
          selectedFutureTagId={selectedFutureTagId}
        />
      </Box>
    </Box>
  );
};

/**
 * EventsGrid Component
 * Handles the display logic for the futures events grid
 */
interface EventsGridProps {
  isLoading: boolean;
  events: any; // Using any for brevity or proper EventResponse[] type
  selectedFutureTagId: number | null;
}

const EventsGrid = ({ isLoading, events, selectedFutureTagId }: EventsGridProps) => {
  if (isLoading) {
    return <SportsFuturesFeedSkeleton showFilters={false} />;
  }

  const safeEvents = Array.isArray(events) ? events : [];

  if (safeEvents.length === 0) {
    return (
      <Box sx={{ py: 10, textAlign: 'center' }}>
        <ErrorState
          title="No Futures Found"
          message="There are currently no active markets for the selected category."
          compact
        />
      </Box>
    );
  }

  const renderSecondaryGridItem = (event: any) => (
    <Grid item xs={12} md={6} lg={4} key={event.id}>
      <SportsFuturesCard
        event={event}
        initialLimit={3}
        showLinearProgress={false}
        selectedFutureTagId={selectedFutureTagId}
      />
    </Grid>
  );

  const renderGlobalFeedItem = (event: any) => (
    <Grid item xs={12} md={6} key={event.id}>
      <SportsFuturesCard event={event} initialLimit={3} selectedFutureTagId={selectedFutureTagId} />
    </Grid>
  );

  const secondaryGridItems = safeEvents.slice(1, 4).map(renderSecondaryGridItem);
  const globalFeedGridItems = safeEvents.slice(4).map(renderGlobalFeedItem);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Featured Highlight (1 card full width) */}
      {safeEvents[0] && (
        <Box sx={{ width: '100%' }}>
          <SportsFuturesCard
            event={safeEvents[0]}
            initialLimit={3}
            allowExpand={true}
            titleSx={{
              fontWeight: 800,
              fontSize: '1.5rem',
            }}
          />
        </Box>
      )}

      {/* Secondary Highlight Grid (up to 3 cards) */}
      {secondaryGridItems.length > 0 && (
        <Grid container spacing={3}>
          {secondaryGridItems}
        </Grid>
      )}

      {/* Global Feed Grid (2 cards per row) */}
      {globalFeedGridItems.length > 0 && (
        <Grid container spacing={3}>
          {globalFeedGridItems}
        </Grid>
      )}
    </Box>
  );
};
